import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCw, AlertTriangle, Network, Users2, ShieldAlert } from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";

const ACCENT = "#22c073";
const GOLD = "#d4af37";
const NAVY = "#0a1018";
const ROSE = "#ef4444";

export default function AffiliateFraud() {
  const [tab, setTab] = useState("self");
  const [selfRefs, setSelfRefs] = useState([]);
  const [sameIp, setSameIp] = useState([]);
  const [multi, setMulti] = useState({ by_phone: [], by_email: [] });
  const [loading, setLoading] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/admin/aff-fraud/self-referrals"),
        api.get("/admin/aff-fraud/same-ip"),
        api.get("/admin/aff-fraud/multiple-accounts"),
      ]);
      setSelfRefs(a.data.rows || []);
      setSameIp(b.data.rows || []);
      setMulti(c.data || { by_phone: [], by_email: [] });
    } catch (e) { toast.error("Failed to load fraud data"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div>
      <PageHeader title="Fraud Protection" subtitle="Self-referral · Same-IP · Multiple-account detection — proactive abuse monitoring for the affiliate / promo code system" />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        <StatCard label="Self-Referrals" value={selfRefs.length} c={ROSE} icon={ShieldAlert} active={tab === "self"} onClick={() => setTab("self")} />
        <StatCard label="Same-IP Clusters" value={sameIp.length} c={GOLD} icon={Network} active={tab === "ip"} onClick={() => setTab("ip")} />
        <StatCard label="Multiple Accounts" value={multi.by_phone.length + multi.by_email.length} c={ACCENT} icon={Users2} active={tab === "multi"} onClick={() => setTab("multi")} />
      </div>

      <div className="flex items-center gap-2 mb-3">
        <button onClick={loadAll} className="h-9 px-3 rounded-md text-[12px] inline-flex items-center gap-1.5 font-semibold" style={{ background: `${ACCENT}1a`, border: `1px solid ${ACCENT}55`, color: ACCENT }} data-testid="fraud-refresh"><RefreshCw size={12} /> Refresh</button>
        <div className="text-[11px] text-white/45">Data updated when you click refresh — checks across all affiliate-referred accounts.</div>
      </div>

      <div className="rounded-xl p-4 text-white" style={{ background: `linear-gradient(180deg, ${NAVY}d9, ${NAVY}f5)`, border: `1px solid ${ACCENT}3a` }}>
        {loading ? <div className="text-white/55 py-10 text-center">Loading…</div>
          : tab === "self" ? <SelfTable rows={selfRefs} />
          : tab === "ip" ? <SameIpTable clusters={sameIp} />
          : <MultiTables data={multi} />}
      </div>
    </div>
  );
}

function StatCard({ label, value, c, icon: Icon, active, onClick }) {
  return (
    <button onClick={onClick} className="rounded-xl p-4 text-left transition-all" style={{
      background: `radial-gradient(400px 120px at 0% 0%, ${c}${active ? "30" : "18"}, transparent 60%), linear-gradient(180deg, ${NAVY}d9, ${NAVY}f5)`,
      border: `1px solid ${c}${active ? "88" : "3a"}`,
      boxShadow: active ? `0 10px 30px ${c}30` : "none",
    }}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/55 font-bold">{label}</div>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${c}18`, border: `1px solid ${c}55`, color: c }}><Icon size={14} /></div>
      </div>
      <div className="text-[24px] font-black" style={{ color: c }}>{value}</div>
    </button>
  );
}

function SelfTable({ rows }) {
  if (rows.length === 0) return <Empty label="No self-referrals detected — clean!" />;
  return (
    <div className="overflow-x-auto">
      <div className="flex items-center gap-2 mb-3"><AlertTriangle size={14} className="text-red-300" /><div className="text-sm font-bold">Users who used their own promo code at registration</div></div>
      <table className="w-full text-[13px] min-w-[700px]" data-testid="fraud-self-table">
        <thead><tr className="text-left text-[10.5px] uppercase tracking-widest" style={{ color: "#fca5a5" }}>
          <th className="py-2 pr-2">Username</th><th className="py-2 pr-2">Email</th><th className="py-2 pr-2">Phone</th><th className="py-2 pr-2">Self Code</th><th className="py-2 pr-2">Registered</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.user_id} style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
              <td className="py-2 pr-2 font-bold">{r.username || "—"}</td>
              <td className="py-2 pr-2 text-white/70 text-xs">{r.email || "—"}</td>
              <td className="py-2 pr-2 text-white/70 text-xs">{r.phone || "—"}</td>
              <td className="py-2 pr-2 font-mono" style={{ color: GOLD }}>{r.self_code}</td>
              <td className="py-2 pr-2 text-white/55 text-xs">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SameIpTable({ clusters }) {
  if (clusters.length === 0) return <Empty label="No same-IP clusters detected" />;
  return (
    <div>
      <div className="flex items-center gap-2 mb-3"><Network size={14} style={{ color: GOLD }} /><div className="text-sm font-bold">IP addresses with 2+ affiliate-referred accounts</div></div>
      <div className="space-y-3" data-testid="fraud-ip-list">
        {clusters.map((c) => (
          <div key={c.ip} className="rounded-lg p-3" style={{ background: "rgba(212,175,55,0.06)", border: `1px solid ${GOLD}30` }}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono font-bold text-sm" style={{ color: GOLD }}>{c.ip}</div>
              <div className="text-[11px] uppercase tracking-widest text-white/55 font-bold">{c.count} accounts</div>
            </div>
            <div className="text-[12px] text-white/75 grid sm:grid-cols-2 gap-x-4">
              {c.users.map((u) => (
                <div key={u.user_id} className="py-1.5 truncate" style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                  <span className="font-bold">{u.username}</span>
                  <span className="ml-2 text-white/45 text-[10.5px]">{(u.created_at || "").slice(0, 10)}</span>
                  {u.code && <span className="ml-2 font-mono text-[10.5px]" style={{ color: ACCENT }}>· {u.code}</span>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MultiTables({ data }) {
  const empty = data.by_phone.length === 0 && data.by_email.length === 0;
  if (empty) return <Empty label="No duplicate phone/email detected" />;
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div>
        <div className="flex items-center gap-2 mb-3"><Users2 size={14} style={{ color: ACCENT }} /><div className="text-sm font-bold">Duplicate phone numbers</div></div>
        {data.by_phone.length === 0 ? <Empty label="None" />
          : data.by_phone.map((g) => (
            <div key={g.value} className="rounded-lg p-3 mb-2" style={{ background: `${ACCENT}10`, border: `1px solid ${ACCENT}30` }}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="font-mono font-bold text-sm" style={{ color: ACCENT }}>{g.value}</div>
                <span className="text-[10.5px] uppercase tracking-widest text-white/55 font-bold">{g.count} accounts</span>
              </div>
              {g.users.map((u) => (
                <div key={u.user_id} className="text-[12px] text-white/75 py-1" style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                  <span className="font-bold">{u.username}</span>
                  <span className="ml-2 text-white/55">{u.email || ""}</span>
                  <span className="ml-2 text-white/45 text-[10.5px]">{(u.created_at || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          ))}
      </div>
      <div>
        <div className="flex items-center gap-2 mb-3"><Users2 size={14} style={{ color: ACCENT }} /><div className="text-sm font-bold">Duplicate emails</div></div>
        {data.by_email.length === 0 ? <Empty label="None" />
          : data.by_email.map((g) => (
            <div key={g.value} className="rounded-lg p-3 mb-2" style={{ background: `${ACCENT}10`, border: `1px solid ${ACCENT}30` }}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="font-mono font-bold text-sm" style={{ color: ACCENT }}>{g.value}</div>
                <span className="text-[10.5px] uppercase tracking-widest text-white/55 font-bold">{g.count} accounts</span>
              </div>
              {g.users.map((u) => (
                <div key={u.user_id} className="text-[12px] text-white/75 py-1" style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                  <span className="font-bold">{u.username}</span>
                  <span className="ml-2 text-white/55">{u.phone || ""}</span>
                  <span className="ml-2 text-white/45 text-[10.5px]">{(u.created_at || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          ))}
      </div>
    </div>
  );
}

function Empty({ label }) {
  return <div className="text-white/55 py-8 text-center text-[13px]">{label}</div>;
}
